import { useState, useEffect, useRef, useCallback } from 'react';
import { EventItem, EventTemplate } from '../core/types.ts';
import { getAuthToken } from './storage.ts';

export interface UserPresence {
  id: string;
  name: string;
  color: string;
  activeTab?: string;
}

export interface RealtimeToast {
  id: string;
  message: string;
  user?: string;
  timestamp: number;
}

const AVATAR_COLORS = [
  '#3b82f6', // blue
  '#ec4899', // pink
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // purple
  '#06b6d4', // cyan
];

function getStoredUser(): UserPresence {
  const storedId = localStorage.getItem('esn_user_id') || `u-${Math.random().toString(36).substring(2, 8)}`;
  const storedName = localStorage.getItem('esn_user_name') || `User ${Math.floor(100 + Math.random() * 900)}`;
  const storedColor = localStorage.getItem('esn_user_color') || AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];

  localStorage.setItem('esn_user_id', storedId);
  localStorage.setItem('esn_user_name', storedName);
  localStorage.setItem('esn_user_color', storedColor);

  return { id: storedId, name: storedName, color: storedColor };
}

export function useRealtimeSync(
  events: EventItem[],
  setEvents: React.Dispatch<React.SetStateAction<EventItem[]>>,
  setTemplates: React.Dispatch<React.SetStateAction<EventTemplate[]>>,
  activeTab: 'calendar' | 'analysis'
) {
  const [currentUser, setCurrentUser] = useState<UserPresence>(getStoredUser);
  const [connectedUsers, setConnectedUsers] = useState<UserPresence[]>([]);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [toasts, setToasts] = useState<RealtimeToast[]>([]);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const isUnmountedRef = useRef<boolean>(false);

  // Keep stable refs for changing props to prevent constant reconnection loops
  const currentUserRef = useRef<UserPresence>(currentUser);
  currentUserRef.current = currentUser;

  const activeTabRef = useRef<string>(activeTab);
  activeTabRef.current = activeTab;

  const setEventsRef = useRef(setEvents);
  setEventsRef.current = setEvents;

  const setTemplatesRef = useRef(setTemplates);
  setTemplatesRef.current = setTemplates;

  const addToast = useCallback((message: string, user?: string) => {
    const newToast: RealtimeToast = {
      id: `toast-${Date.now()}-${Math.random()}`,
      message,
      user,
      timestamp: Date.now(),
    };
    setToasts(prev => [newToast, ...prev].slice(0, 5));
  }, []);

  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const useFallbackPortRef = useRef<boolean>(false);

  const connect = useCallback(() => {
    if (isUnmountedRef.current) return;
    if (socketRef.current?.readyState === WebSocket.OPEN || socketRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      let wsUrl = `${protocol}//${window.location.host}/api/ws`;

      if (useFallbackPortRef.current && window.location.port !== '8000') {
        const hostname = window.location.hostname || 'localhost';
        wsUrl = `${protocol}//${hostname}:8000/api/ws`;
      }

      const authToken = getAuthToken();
      if (authToken) {
        const separator = wsUrl.includes('?') ? '&' : '?';
        wsUrl = `${wsUrl}${separator}token=${encodeURIComponent(authToken)}`;
      }

      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;

      ws.onopen = () => {
        if (isUnmountedRef.current) {
          ws.close();
          return;
        }
        setIsConnected(true);
        ws.send(
          JSON.stringify({
            type: 'JOIN',
            user: { ...currentUserRef.current, activeTab: activeTabRef.current },
          })
        );
      };

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);

          if (data.type === 'INIT_STATE') {
            if (Array.isArray(data.events) && data.events.length > 0) {
              setEventsRef.current(data.events);
            }
            if (Array.isArray(data.templates)) {
              setTemplatesRef.current(data.templates);
            }
            if (Array.isArray(data.users)) {
              setConnectedUsers(data.users);
            }
          } else if (data.type === 'USERS_UPDATE') {
            if (Array.isArray(data.users)) {
              setConnectedUsers(data.users);
            }
          } else if (data.type === 'EVENTS_UPDATED') {
            if (Array.isArray(data.events)) {
              setEventsRef.current(data.events);
              const author = data.actionUser || 'A collaborator';
              const detail = data.actionDetail || 'updated the schedule live';
              addToastRef.current(`${detail}`, author);
            }
          } else if (data.type === 'TEMPLATES_UPDATED') {
            if (Array.isArray(data.templates)) {
              setTemplatesRef.current(data.templates);
              const author = data.actionUser || 'A collaborator';
              addToastRef.current('updated templates', author);
            }
          }
        } catch (err) {
          console.error('Error handling WebSocket message:', err);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        socketRef.current = null;
        if (!isUnmountedRef.current) {
          useFallbackPortRef.current = !useFallbackPortRef.current;
          reconnectTimeoutRef.current = window.setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => {
        setIsConnected(false);
      };
    } catch (_e) {
      setIsConnected(false);
    }
  }, []);

  // Open socket connection once on component mount
  useEffect(() => {
    isUnmountedRef.current = false;
    connect();
    return () => {
      isUnmountedRef.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [connect]);

  // Update presence on existing connection when activeTab or currentUser changes
  useEffect(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: 'PRESENCE',
          user: { ...currentUser, activeTab },
        })
      );
    }
  }, [activeTab, currentUser]);

  const updateUserName = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const updated = { ...currentUser, name: trimmed };
    setCurrentUser(updated);
    localStorage.setItem('esn_user_name', trimmed);

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: 'PRESENCE',
          user: { ...updated, activeTab },
        })
      );
    }
  };

  const broadcastEventsChange = (updatedEvents: EventItem[], actionDetail?: string) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: 'MUTATE_EVENTS',
          events: updatedEvents,
          actionUser: currentUser.name,
          actionDetail: actionDetail || 'updated the schedule live',
        })
      );
    }
  };

  const broadcastTemplatesChange = (updatedTemplates: EventTemplate[]) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: 'MUTATE_TEMPLATES',
          templates: updatedTemplates,
          actionUser: currentUser.name,
        })
      );
    }
  };

  return {
    currentUser,
    connectedUsers,
    isConnected,
    toasts,
    dismissToast,
    updateUserName,
    broadcastEventsChange,
    broadcastTemplatesChange,
  };
}
